import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSearchParams } from 'react-router-dom';
import { generateEmailLink } from '@/utils/email-templates';
import { Logo } from '@/components/logos/logo';
import { useMemo } from 'react';
import { ERROR_GIFS } from '@/utils/error-gifs';

interface ErrorPageProps {
  title?: string;
  message?: string;
}

export function ErrorPage({ title, message }: ErrorPageProps) {
  const [searchParams] = useSearchParams();

  // Get error details from URL params or use props
  const errorTitle = searchParams.get('title') ?? title ?? 'Something went wrong';
  const errorMessage = searchParams.get('message') ?? message ?? 'An unexpected error occurred.';
  const additionalInfo = searchParams.get('additionalInfo');
  const error = searchParams.get('error');

  // Randomly select a GIF - memoized so it doesn't change on re-renders
  const selectedGif = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * ERROR_GIFS.length);
    return ERROR_GIFS[randomIndex];
  }, []);

  const handleContactSupport = () => {
    const emailLink = generateEmailLink({
      subject: `Error: ${errorTitle}`,
      body: `I encountered an error while using Contextual:\n\nError: ${errorTitle}\nMessage: ${errorMessage}${additionalInfo ? `\nAdditional Info: ${additionalInfo}` : ''}${error ? `\nError Details: ${error}` : ''}`
    });
    window.location.href = emailLink;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader className="text-center space-y-4 pb-2">
          <div className="mx-auto">
            <Logo icon="contextual" width={36} height={36} />
          </div>
          <CardTitle className="text-2xl font-bold">{errorTitle}</CardTitle>
          <CardDescription className="text-base">{errorMessage}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Randomly selected GIF */}
          <div className="flex justify-center">
            <img
              src={selectedGif.url}
              alt={selectedGif.alt}
              className="rounded-lg max-w-[300px] h-auto"
            />
          </div>

          {additionalInfo && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium text-sm text-gray-700">Additional Information:</p>
              <p className="text-sm text-gray-600 mt-1">{additionalInfo}</p>
            </div>
          )}

          {error && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium text-sm text-gray-700">Error Details:</p>
              <p className="text-sm font-mono text-gray-600 mt-1 break-all">{error}</p>
            </div>
          )}

          <div className="flex justify-center pt-4">
            <Button onClick={handleContactSupport}>Contact Support</Button>
          </div>
        </CardContent>
      </Card>
      <p className="text-gray-500 text-sm mt-6">
        Need immediate help? Email us at support@getcontextual.io
      </p>
    </div>
  );
}
